import { ProjectsSelect, ProjectTagMultiSelect } from "@selector/shared/components";
import { atom, useAtom, useAtomValue, useSetAtom } from "jotai";
import { find, flatten, groupBy, keys, map, prop, propEq, reject } from "ramda";
import { type FC, useCallback, useEffect, useState } from "react";
import { Navigate, Route, Routes, useNavigate, useParams } from "react-router";
import { Item, useAsyncList } from "react-stately";
import { titleCase } from "title-case";
import {
  Card,
  CardSection,
  LoadingSpinner,
  MenuOptionItem,
  PanelGroup,
  PanelOrientation,
  PanelPosition,
  SelectList,
  Spacer,
  Tag,
  TagIntent,
  TriggerButton,
  VStack,
} from "widgets";
import type { GetArrayElementType } from "widgets/utils";

import { useError } from "@/app-utils/graphql/useError.ts";
import { pluralizeWord } from "@/app-utils/string.ts";
import { ToggleFiltersButton } from "@/modules/core/components/ToggleFiltersButton.tsx";
import { ParentsView } from "@/modules/saved-items/components/ParentsView.tsx";
import { SavedItemsView } from "@/modules/saved-items/components/SavedItemsView";
import {
  type GetSavedItemsListingQuery,
  useGetSavedItemsListingLazyQuery,
} from "@/modules/saved-items/graphql/saved-items.gql";
import { savedItemHeaderInfoAtom } from "@/modules/saved-items/store/atoms.ts";
import style from "@/modules/workflow/styles/ToolBar.module.css";
import { ITEMS_TYPE } from "@/settings/settings.ts";
import { ContentPlaceholder } from "@/ui-modules/core/ContentPlaceholder.tsx";

export function SavedItemsPage() {
  return (
    <VStack spacing={0} style={{ gridArea: "page", overflow: "hidden" }}>
      <PanelGroup
        key={"saved-items"}
        orientation={PanelOrientation.Horizontal}
        isContinuous
        defaultSize={280}
        minSize={240}
        maxSize={500}
        fixedPanel={PanelPosition.Leading}
      >
        <Routes>
          <Route index element={<Navigate to={"view/view"} replace />} />
          <Route path=":type/:subType" element={<SavedItemsNavigationPanel />} />
        </Routes>
        <Routes>
          <Route path=":type/:subType" element={<ContentBrowserSavedItemsPage />} />
        </Routes>
      </PanelGroup>
    </VStack>
  );
}

const tagIdsAtom = atom<number[]>([]);
const projectIdAtom = atom<number | undefined>(undefined);

type Listing = GetArrayElementType<GetSavedItemsListingQuery["savedItems"]["listing"]>;
export type Item = Listing & { id: string };

export interface List {
  name: string;
  items: Item[];
}

const SavedItemsNavigationPanel: FC = () => {
  const navigate = useNavigate();
  const { type, subType } = useParams<"type" | "subType">();

  const [tagIds, setTagIds] = useAtom(tagIdsAtom);
  const [projectId, setProjectId] = useAtom(projectIdAtom);
  const setSavedItemHeaderInfo = useSetAtom(savedItemHeaderInfoAtom);

  const [fetch, { error, loading }] = useGetSavedItemsListingLazyQuery();
  void useError(error);

  const list = useAsyncList<List>({
    async load() {
      try {
        const { data } = await fetch({
          variables: { projectId: projectId ?? -1, tagIds: tagIds.length > 0 ? tagIds : null },
        });
        const listing = reject(propEq(0, "count"), data.savedItems.listing);
        const grouped = groupBy((item) => (item.type === "parent" ? "parent" : "other"), listing);
        const items = map(
          (key) => ({
            name: key,
            items: map((item) => ({ id: `${item.type}_${item.subType}`, ...item }), grouped[key]!),
          }),
          keys(grouped),
        );

        return { items };
      } catch (err) {
        return { items: [] };
      }
    },
  });

  const getItem = useCallback(
    (key: string) => {
      const flatList = flatten(map(prop("items"), list.items));
      return find(propEq(key, "id"), flatList);
    },
    [list.items],
  );

  useEffect(() => {
    if (type && subType) {
      list.setSelectedKeys(new Set([`${type}_${subType}`]));
    }
  }, [type, subType]);

  useEffect(() => {
    list.reload();
  }, [tagIds, projectId]);

  useEffect(() => {
    if (loading) {
      setSavedItemHeaderInfo({ title: null, count: null });
    } else {
      const item = getItem([...list.selectedKeys][0]);
      if (item) {
        setSavedItemHeaderInfo({
          title: item.name,
          count: item.count === item.filteredCount ? item.count : item.filteredCount,
        });
      }
    }
  }, [list.selectedKeys, getItem, loading]);

  const sampleParentItems = find<List>(propEq("parent", "name"), list.items)?.items ?? [];
  const otherSavedItems = find<List>(propEq("other", "name"), list.items)?.items ?? [];

  useEffect(
    () => () => {
      // reset
      setTagIds([]);
      setProjectId(undefined);
      setSavedItemHeaderInfo({ title: null, count: null });
    },
    [],
  );

  const [areFiltersVisible, setFiltersVisible] = useState<boolean>(false);

  return (
    <Card
      noBorder
      fillHeight
      scrollable
      style={{ backgroundColor: "var(--panel-background)" }}
      className={style.header}
      title="Saved Items"
      actions={[
        <Spacer key="spacer" />,
        <TriggerButton key="workflow-refetch-widget" icon={"sync"} label={"Refresh"} onPress={() => list.reload()} />,
      ]}
    >
      <CardSection
        title="Views, Groups, Flags, Lists"
        actions={[
          <ToggleFiltersButton
            key="toggle-filters"
            areFiltersVisible={areFiltersVisible}
            toggleFiltersVisibility={() => {
              if (projectId || tagIds?.length) {
                setTagIds([]);
                setProjectId(undefined);
              }
              setFiltersVisible((toggle) => !toggle);
            }}
          />,
        ]}
      >
        {areFiltersVisible && (
          <VStack style={{ flex: "0 0 auto" }} spacing={10}>
            <ProjectsSelect
              includeAllProjects
              label="Filter by project"
              isRequired
              initialSelectedProjectId={projectId}
              onSelectionChange={(project) => {
                if (project == undefined) {
                  setProjectId(undefined);
                } else if (project?.id === -1) {
                  // All projects
                  setProjectId(undefined);
                } else {
                  setProjectId(project.id);
                }
              }}
              defaultSelection={"all"}
            />
            <ProjectTagMultiSelect
              label="Filter by tags"
              projectId={projectId}
              isRequired
              onSelectionChange={setTagIds}
              selectedIds={tagIds}
            />
          </VStack>
        )}
        {list.isLoading ? (
          <LoadingSpinner />
        ) : (
          <SelectList
            noBorder
            style={{ margin: "0 -5px" }}
            items={otherSavedItems}
            selectedKeys={list.selectedKeys}
            onSelectionChange={(keys) => {
              if (keys instanceof Set) {
                const item = getItem([...keys][0]);
                if (item) {
                  list.setSelectedKeys(keys);
                  navigate(`../${item.type}/${encodeURIComponent(item.subType)}`);
                }
              }
            }}
            selectionMode="single"
            disallowEmptySelection
          >
            {(item) => (
              <Item key={item.id}>
                <MenuOptionItem
                  title={titleCase(pluralizeWord(item.name))}
                  tag={
                    item.count === item.filteredCount ? (
                      item.count
                    ) : (
                      <Tag
                        label={item.filteredCount}
                        intentColor={item.filteredCount === 0 ? TagIntent.Secondary : TagIntent.Primary}
                      />
                    )
                  }
                  useHover
                />
              </Item>
            )}
          </SelectList>
        )}
      </CardSection>
      {!loading && otherSavedItems.length === 0 && (
        <ContentPlaceholder mini>
          When you are working, look for the option to save pages, flags, genomes, etc. so that you can access, organize
          or analyze them from here in the future.
        </ContentPlaceholder>
      )}
      {sampleParentItems.length > 0 && (
        <CardSection title={"Sample Parents"}>
          {list.isLoading ? (
            <LoadingSpinner />
          ) : (
            <SelectList
              noBorder
              style={{ margin: "0 -5px" }}
              items={sampleParentItems}
              selectedKeys={list.selectedKeys}
              onSelectionChange={(keys) => {
                if (keys instanceof Set) {
                  const item = getItem([...keys][0]);
                  if (item) {
                    list.setSelectedKeys(keys);
                    navigate(`../${item.type}/${encodeURIComponent(item.subType)}`);
                  }
                }
              }}
              selectionMode="single"
              disallowEmptySelection
            >
              {(item) => (
                <Item key={item.id}>
                  <MenuOptionItem title={titleCase(pluralizeWord(item.name))} tag={item.count} noCheck useHover />
                </Item>
              )}
            </SelectList>
          )}
        </CardSection>
      )}
    </Card>
  );
};

const ContentBrowserSavedItemsPage: FC = () => {
  const { type, subType } = useParams<"type" | "subType">();
  const tagIds = useAtomValue(tagIdsAtom);
  const projectId = useAtomValue(projectIdAtom);

  if (type === ITEMS_TYPE.SAMPLE_PARENT) {
    return <ParentsView type={type} subType={subType as string} />;
  } else {
    return (
      <SavedItemsView
        type={type as string}
        subType={subType as string}
        keepProjectIcon
        projectId={projectId}
        tagIds={tagIds}
      />
    );
  }
};
